import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const sundbybergPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-sundbyberg",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Sundbyberg – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Sundbyberg. Vi hämtar möbler, vitvaror och skräp i Centrala Sundbyberg, Hallonbergen, Rissne, Ör, Duvbo och Ursvik. Fast pris, ofta samma dag.",
      h1: "Bortforsling Sundbyberg",
    },

    hero: {
      heading: "Bortforsling i Sundbyberg",
      intro:
        "Boka bortforsling i Sundbyberg – vi hämtar möbler, vitvaror och skrymmande föremål i hela staden, från höghusen i Hallonbergen och Ör till de nya kvarteren i Ursvik och villorna i Duvbo. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Sundbyberg – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Sundbyberg",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Sundbyberg och körs till grovavfallspunkten i Rissne",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från lägenhet i Centrala Sundbyberg",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Hallonbergen, Sundbyberg",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Ursvik, Sundbyberg",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Rissne, Sundbyberg",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Duvbo, Sundbyberg",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Sundbyberg – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Sundbyberg är Sveriges till ytan minsta kommun och samtidigt en av de tätaste – en riktig knutpunkt där tunnelbanans blå linje, pendeltåg, tvärbana, regionaltåg och bussar möts i centrum. Bebyggelsen är till största delen lägenheter: miljonprogrammets höghus i Hallonbergen och Ör, åttiotalskvarteren i Rissne, det täta Centrala Sundbyberg kring Landsvägen och Marabouparken, samt de nybyggda stadsdelarna Stora Ursvik och Brotorp. Undantaget är trädgårdsstaden Duvbo med sina villor. Den täta stadsmiljön gör bortforsling till en utmaning om man försöker klara den själv: trånga trapphus, små hissar, begränsad gatuparkering och många bostadsrättsföreningar med egna lastregler. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Sundbyberg är en av delägarkommunerna i SÖRAB, och har dessutom en stor fördel i att grovavfallspunkten på Valkyriavägen i Rissne ligger mitt i kommunen. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E18 passerar i norr och tvärbanan och tunnelbanan binder ihop staden tar vi oss snabbt mellan stadsdelarna – trots den pågående utbyggnaden av Mälarbanan i centrala Sundbyberg. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både punkthusen i Hallonbergen och de smala gatorna i Duvbo – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Sundbyberg",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i lägenhet i Hallonbergen, Ör, Rissne eller Centrala Sundbyberg, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning – i den täta Sundbybergsbebyggelsen sparar det mycket tid jämfört med att leta plats på gatan. Bor du i villa i Duvbo eller radhus i Brotorp är det smidigt om vi kan komma nära entrén eller garaget.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du som Sundbybergsbo gratis på grovavfallspunkten på Valkyriavägen eller på Returpunkten på Landsvägen.",
      additionalSections: [
        {
          heading: "Stadsdelar och områden vi täcker i Sundbyberg",
          content:
            "Vi utför bortforsling i hela Sundbyberg – från de etablerade kvarteren till de nybyggda stadsdelarna. Oavsett om du bor i en lägenhet i Hallonbergen, en villa i Duvbo, en nybyggd etta i Stora Ursvik eller en bostadsrätt i Centrala Sundbyberg kan vi vara på plats, ofta redan samma dag.\n\nI Centrala Sundbyberg – kring Landsvägen, Sundbybergs torg och Marabouparken – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, ofta i bostadsrättsföreningar med egna last- och parkeringsregler, och vi planerar runt arbetena med Mälarbanan. I miljonprogramsområdena Hallonbergen och Ör känner vi till punkthusen och de stora bostadsgårdarna, och i Rissne de något yngre åttiotalskvarteren.\n\nVi är minst lika snabba i stadens nyare och lugnare delar: Stora Ursvik och Brotorp med sina nybyggda kvarter, trädgårdsstaden Duvbo med villor, samt Lilla Alby och Storskogen. Tack vare närheten till E18, tvärbanan och tunnelbanans blå linje planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon stadsdel.",
        },
        {
          heading: "Återvinning och miljöansvar i Sundbyberg",
          content:
            "En viktig del av vår bortforsling i Sundbyberg är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Sundbyberg är en av delägarkommunerna i SÖRAB, och de anläggningar vi använder mest för uppdrag i staden är:\n\nGrovavfallspunkten på Valkyriavägen i Rissne – bemannad dagligen och avsedd för större mängder grovavfall, perfekt placerad mitt i kommunen. Returpunkten på Landsvägen – ett miniåtervinningscenter för återbruk och mindre mängder. SÖRAB:s mobila återvinningscentral – som ställs upp på kvällar på flera platser i Sundbyberg. Vi väljer alltid den lösning som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Sundbyberg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Sundbybergs stadsdelar, från punkthusen i Hallonbergen och Ör till villorna i Duvbo och de nya kvarteren i Ursvik, och vet hur man löser lastning i tät stadsmiljö och bostadsrättsföreningar. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 passerar strax norr om Sundbyberg. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon stadsdel i Sundbyberg, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din innergård, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via grovavfallspunkten på Valkyriavägen och Returpunkten. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Sundbyberg?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon stadsdel i Sundbyberg.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Sundbyberg?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Sundbyberg?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen, och Sundbyberg är litet och centralt, så vi tar oss snabbt mellan stadsdelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Sundbyberg?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Sundbyberg?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i Sundbybergs täta bebyggelse, från punkthusen i Hallonbergen och Ör till bostadsrätterna i Centrala Sundbyberg. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni hämta i bostadsrättsförening med särskilda lastregler i Sundbyberg?",
        answer:
          "Ja, vi är vana vid Sundbybergs många bostadsrättsföreningar och deras regler för lastning, innergårdar och gästparkering. Meddela oss vid bokningen om det finns en särskild lastzon eller tider att ta hänsyn till, så planerar vi därefter.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Sundbyberg försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din innergård och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Sundbyberg?",
        answer:
          "Vi sorterar allt material och kör det till godkända återvinningscentraler. Sundbyberg är en av delägarkommunerna i SÖRAB, så större mängder körs till grovavfallspunkten på Valkyriavägen i Rissne medan mindre mängder och återbruk lämnas på Returpunkten på Landsvägen. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Sundbyberg på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Sundbyberg?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid de gemensamma förrådsutrymmena i Sundbybergs bostadsrättsföreningar och flerbostadshus.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Sundbyberg?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Centrala Sundbyberg och ditt förråd på en annan adress i staden.",
      },
      {
        question: "Vilka stadsdelar i Sundbyberg täcker ni?",
        answer:
          "Vi utför bortforsling i hela Sundbyberg – Centrala Sundbyberg, Lilla Alby, Duvbo, Storskogen, Ör, Hallonbergen, Rissne, Stora Ursvik och Brotorp. Inga extra avgifter för någon stadsdel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Sundbyberg?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du som Sundbybergsbo gratis på grovavfallspunkten på Valkyriavägen eller på Returpunkten på Landsvägen.",
      },
      {
        question: "Vilka kommuner runt Sundbyberg utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Sundbyberg i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Sundbyberg kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och bostadsrättsföreningarnas lastregler – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Sundbyberg idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Sundbyberg.",
    },

    en: {
      slug: "junk-removal-sundbyberg",
      meta: {
        title: "Junk Removal Sundbyberg – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Sundbyberg. We pick up furniture, appliances and bulky items in Central Sundbyberg, Hallonbergen, Rissne, Ör, Duvbo and Ursvik. Fixed price, often same day.",
        h1: "Junk Removal Sundbyberg",
      },
      hero: {
        heading: "Junk Removal in Sundbyberg",
        intro:
          "Book junk removal in Sundbyberg – we collect furniture, appliances and bulky items across the whole city, from the high-rises of Hallonbergen and Ör to the new quarters of Ursvik and the villas of Duvbo. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Sundbyberg – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Sundbyberg",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Sundbyberg and driven to the bulky-waste point in Rissne",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from an apartment in Central Sundbyberg",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Hallonbergen, Sundbyberg",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Ursvik, Sundbyberg",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Rissne, Sundbyberg",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Duvbo, Sundbyberg",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Sundbyberg – fast, hassle-free and responsible",
        mainText:
          "Sundbyberg is Sweden's smallest municipality by area and at the same time one of the densest – a real transport hub where the blue metro line, commuter trains, the tvärbanan tram, regional trains and buses all meet in the centre. The housing is mostly apartments: the 'miljonprogram' high-rises of Hallonbergen and Ör, the 1980s blocks of Rissne, the dense Central Sundbyberg around Landsvägen and Marabouparken, and the newly built districts of Stora Ursvik and Brotorp. The exception is the garden suburb of Duvbo with its villas. The dense urban setting makes junk removal a challenge if you try to do it yourself: narrow stairwells, small elevators, limited street parking and many housing cooperatives with their own loading rules. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Sundbyberg is one of the part-owner municipalities of SÖRAB, and has a big advantage in that the bulky-waste point on Valkyriavägen in Rissne is located right in the middle of the municipality. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E18 passes to the north and the tvärbanan and metro tie the city together, we move quickly between districts – despite the ongoing Mälarbanan railway works in central Sundbyberg. This means we can often be on site the same day you book. Our workers are used to both the point blocks of Hallonbergen and the narrow streets of Duvbo – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Sundbyberg",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in an apartment in Hallonbergen, Ör, Rissne or Central Sundbyberg, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use – in Sundbyberg's dense layout that saves a lot of time compared to searching for a spot on the street. If you live in a villa in Duvbo or a townhouse in Brotorp, it helps if we can get close to the entrance or garage.\n\nGather the items in one place if possible, for example in the hallway or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Sundbyberg resident you can drop those off for free at the bulky-waste point on Valkyriavägen or at the Returpunkten on Landsvägen.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Sundbyberg",
            content:
              "We perform junk removal across the whole of Sundbyberg – from the established quarters to the newly built districts. Whether you live in an apartment in Hallonbergen, a villa in Duvbo, a new studio in Stora Ursvik or a cooperative flat in Central Sundbyberg, we can be on site, often the same day.\n\nIn Central Sundbyberg – around Landsvägen, Sundbybergs torg and Marabouparken – we are highly experienced with apartment pickups via elevator and stairwell, often in housing cooperatives with their own loading and parking rules, and we plan around the Mälarbanan railway works. In the 'miljonprogram' areas of Hallonbergen and Ör we know the point blocks and large residential courtyards, and in Rissne the somewhat younger 1980s quarters.\n\nWe are just as quick in the newer and quieter parts of the city: Stora Ursvik and Brotorp with their newly built quarters, the garden suburb of Duvbo with villas, and Lilla Alby and Storskogen. Thanks to the proximity of the E18, the tvärbanan tram and the blue metro line, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Sundbyberg",
            content:
              "An important part of our junk removal in Sundbyberg is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Sundbyberg is one of the part-owner municipalities of SÖRAB, and the facilities we use most for jobs in the city are:\n\nThe bulky-waste point on Valkyriavägen in Rissne – staffed daily and intended for larger amounts of bulky waste, perfectly placed in the middle of the municipality. The Returpunkten on Landsvägen – a miniature recycling centre for reuse and smaller amounts. SÖRAB's mobile recycling centre – which is set up in the evenings at several locations in Sundbyberg. We always choose the solution nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Sundbyberg with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Sundbyberg's districts, from the point blocks of Hallonbergen and Ör to the villas of Duvbo and the new quarters of Ursvik, and how to solve loading in a dense urban environment and in housing cooperatives. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E18 passes just north of Sundbyberg. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Sundbyberg, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your courtyard, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the bulky-waste point on Valkyriavägen and the Returpunkten. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Sundbyberg cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Sundbyberg.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Sundbyberg?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Sundbyberg?",
          answer:
            "We have vans and staff across the Stockholm region, and Sundbyberg is small and central, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Sundbyberg?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Sundbyberg?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in Sundbyberg's dense buildings, from the point blocks of Hallonbergen and Ör to the cooperative flats in Central Sundbyberg. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you collect from a housing cooperative with special loading rules in Sundbyberg?",
          answer:
            "Yes, we are used to Sundbyberg's many housing cooperatives (bostadsrättsföreningar) and their rules for loading, courtyards and visitor parking. Let us know at booking if there is a specific loading zone or times to consider, and we plan accordingly.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Sundbyberg insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, courtyard and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Sundbyberg?",
          answer:
            "We sort all materials and drive them to approved recycling centres. Sundbyberg is one of the part-owner municipalities of SÖRAB, so larger amounts go to the bulky-waste point on Valkyriavägen in Rissne while smaller amounts and reusable items go to the Returpunkten on Landsvägen. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Sundbyberg on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Sundbyberg?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to the shared storage rooms in Sundbyberg's housing cooperatives and apartment buildings.",
        },
        {
          question: "Can you make multiple stops during junk removal in Sundbyberg?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Central Sundbyberg and a storage unit at another address in the city, for example.",
        },
        {
          question: "Which districts in Sundbyberg do you cover?",
          answer:
            "We perform junk removal across all of Sundbyberg – Central Sundbyberg, Lilla Alby, Duvbo, Storskogen, Ör, Hallonbergen, Rissne, Stora Ursvik and Brotorp. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Sundbyberg?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Sundbyberg resident you can drop those off for free at the bulky-waste point on Valkyriavägen or at the Returpunkten on Landsvägen.",
        },
        {
          question: "Which municipalities around Sundbyberg do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Sundbyberg in 3 easy steps",
        step2Text:
          "Our young workers in Sundbyberg come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and housing-cooperative loading rules – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Sundbyberg today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole of Sundbyberg.",
      },
    },
  },
];
